import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNostr } from '@nostrify/react';
import { Zap, Trophy, Medal, Award } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuthor } from '@/hooks/useAuthor';
import { genUserName } from '@/lib/genUserName';
import type { NostrEvent } from '@nostrify/nostrify';

interface AppSupportersProps {
  eventId: string;
}

interface ZapData {
  pubkey: string;
  amount: number;
  count: number;
}

export function AppSupporters({ eventId }: AppSupportersProps) {
  const { nostr } = useNostr();

  const { data: zaps, isLoading } = useQuery({
    queryKey: ['app-zaps', eventId],
    queryFn: async (c) => {
      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(3000)]);

      // Query zap receipts (kind 9735) for this event
      const zapReceipts = await nostr.query(
        [
          {
            kinds: [9735],
            '#e': [eventId],
            limit: 500,
          },
        ],
        { signal }
      );

      return zapReceipts;
    },
  });

  // Aggregate zaps by sender
  const supporters = useMemo(() => {
    if (!zaps || zaps.length === 0) return [];

    const aggregated = new Map<string, ZapData>();

    for (const zap of zaps) {
      // Extract amount from bolt11 invoice
      const bolt11Tag = zap.tags.find(([name]) => name === 'bolt11')?.[1];
      const descriptionTag = zap.tags.find(([name]) => name === 'description')?.[1];

      if (!bolt11Tag || !descriptionTag) continue;

      // Parse the zap request from description to get sender
      let zapRequest: NostrEvent;
      try {
        zapRequest = JSON.parse(descriptionTag);
      } catch {
        continue;
      }

      const sender = zapRequest.pubkey;

      // Extract amount from bolt11 (amount is in millisats)
      const amountMatch = bolt11Tag.match(/lnbc(\d+)[munp]/);
      if (!amountMatch) continue;

      const multipliers: Record<string, number> = {
        m: 0.001, // milli = /1000
        u: 0.000001, // micro = /1000000
        n: 0.000000001, // nano = /1000000000
        p: 0.000000000001, // pico = /1000000000000
      };

      const unit = amountMatch[0].slice(-1);
      const multiplier = multipliers[unit] || 1;
      const amountInBTC = parseInt(amountMatch[1]) * multiplier;
      const amountInSats = Math.floor(amountInBTC * 100000000);

      const existing = aggregated.get(sender);
      if (existing) {
        existing.amount += amountInSats;
        existing.count += 1;
      } else {
        aggregated.set(sender, {
          pubkey: sender,
          amount: amountInSats,
          count: 1,
        });
      }
    }

    return Array.from(aggregated.values())
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 10); // Top 10 supporters
  }, [zaps]);

  if (isLoading) {
    return (
      <Card className="bg-gradient-to-br from-black/40 via-purple-500/5 to-purple-900/10 border-purple-500/20 backdrop-blur-md">
        <CardHeader>
          <CardTitle className="text-lg text-purple-100 flex items-center gap-2">
            <Zap className="h-5 w-5 text-yellow-400" />
            Top Supporters
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton className="h-10 w-10 rounded-full" />
              <div className="flex-1 space-y-1">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-3 w-16" />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }

  if (!supporters || supporters.length === 0) {
    return (
      <Card className="bg-gradient-to-br from-black/40 via-purple-500/5 to-purple-900/10 border-purple-500/20 backdrop-blur-md">
        <CardHeader>
          <CardTitle className="text-lg text-purple-100 flex items-center gap-2">
            <Zap className="h-5 w-5 text-yellow-400" />
            Top Supporters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-purple-300/60 text-center py-4">
            Be the first to support this app! ⚡
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-gradient-to-br from-black/40 via-purple-500/5 to-purple-900/10 border-purple-500/20 backdrop-blur-md">
      <CardHeader>
        <CardTitle className="text-lg text-purple-100 flex items-center gap-2">
          <Zap className="h-5 w-5 text-yellow-400 animate-pulse" />
          Top Supporters
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {supporters.map((supporter, index) => (
          <SupporterItem
            key={supporter.pubkey}
            pubkey={supporter.pubkey}
            amount={supporter.amount}
            count={supporter.count}
            rank={index + 1}
          />
        ))}
      </CardContent>
    </Card>
  );
}

interface SupporterItemProps {
  pubkey: string;
  amount: number;
  count: number;
  rank: number;
}

function SupporterItem({ pubkey, amount, count, rank }: SupporterItemProps) {
  const author = useAuthor(pubkey);
  const metadata = author.data?.metadata;

  const displayName = metadata?.name || metadata?.display_name || genUserName(pubkey);
  const avatar = metadata?.picture;

  const getRankIcon = () => {
    switch (rank) {
      case 1:
        return <Trophy className="h-5 w-5 text-yellow-400" />;
      case 2:
        return <Medal className="h-5 w-5 text-gray-300" />;
      case 3:
        return <Award className="h-5 w-5 text-orange-400" />;
      default:
        return <span className="text-sm font-semibold text-purple-300/50">#{rank}</span>;
    }
  };

  const formatSats = (sats: number) => {
    if (sats >= 1000000) {
      return `${(sats / 1000000).toFixed(1)}M`;
    } else if (sats >= 1000) {
      return `${(sats / 1000).toFixed(1)}K`;
    }
    return sats.toLocaleString();
  };

  return (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-black/20 hover:bg-purple-500/10 transition-colors border border-purple-500/10 hover:border-purple-500/20 group">
      <div className="flex items-center justify-center w-8">
        {getRankIcon()}
      </div>

      <Avatar className="h-10 w-10 ring-2 ring-purple-500/20 group-hover:ring-purple-500/40 transition-all">
        <AvatarImage src={avatar} alt={displayName} />
        <AvatarFallback className="bg-purple-900/30 text-purple-300 text-sm">
          {displayName.slice(0, 2).toUpperCase()}
        </AvatarFallback>
      </Avatar>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-purple-200 group-hover:text-purple-100 truncate">
          {displayName}
        </p>
        <p className="text-xs text-purple-300/50">
          {count} {count === 1 ? 'zap' : 'zaps'}
        </p>
      </div>

      <Badge className="bg-gradient-to-r from-yellow-600/20 to-orange-600/20 text-yellow-200 border-yellow-500/30 hover:from-yellow-600/30 hover:to-orange-600/30">
        <Zap className="h-3 w-3 mr-1" />
        {formatSats(amount)}
      </Badge>
    </div>
  );
}
